import type BaseEnumType from "@/app/services/baseEnumType";
import { EnumVesselMovementSourceType } from "@/app/services/generated/enums/enumVesselMovementSourceType.generated";
import { EnumVesselMovementTargetType } from "@/app/services/generated/enums/enumVesselMovementTargetType.generated";
import type { VesselMovement } from "@/app/services/generated/vesselmovementService";
import { ToVesselDetails } from "@/app/services/generated/vesselmovementService";
import Key from "@/app/types/common/key";
import type { Maybe } from "@/components/utils/typeHelpers";
import { translate } from "@/plugins/i18n";
import { useRuleSets } from "@/plugins/validation";
import type { Ref } from "vue";
import { computed, ref, watch } from "vue";

export function movementTypeRequiresToVessel(
  type: EnumVesselMovementType,
  optionalOutputChosen: boolean
) {
  switch (type) {
    case EnumVesselMovementType.updateVolume:
    case EnumVesselMovementType.vesselToVessel:
    case EnumVesselMovementType.addIngredientsToVessel:
      return true;

    case EnumVesselMovementType.vesselToExistingBatch:
    case EnumVesselMovementType.vesselToExistingPackaging:
    case EnumVesselMovementType.vesselToNewBatch:
    case EnumVesselMovementType.vesselToNewPackaging:
      return optionalOutputChosen;

    default:
      return false;
  }
}

export function movementTypeRequiresFromVessel(type: EnumVesselMovementType) {
  return type.sourceType.isVessel();
}

export function useVesselMovementState(
  vesselMovement: Ref<VesselMovement>,
  vesselMovementType: Ref<EnumVesselMovementType>
) {
  const { ruleSet } = useRuleSets();
  const includeVesselMovementWithOutput = ref(
    vesselMovementType.value.prefersToTargetVessel
  );

  const movementRequiresToVessel = computed(() =>
    movementTypeRequiresToVessel(
      vesselMovementType.value,
      includeVesselMovementWithOutput.value
    )
  );

  const movementRequiresFromVessel = computed(() =>
    movementTypeRequiresFromVessel(vesselMovementType.value)
  );

  // Require at least sourceFinalLitres or sourceLitresLeaving to be defined at minimum
  const fromVesselVolumeIsRequired = computed(() => {
    return (vesselMovement.value.fromVesselDetails?.sourceFinalLitres ??
      vesselMovement.value.fromVesselDetails?.sourceLitresLeaving)
      ? []
      : [ruleSet.required()];
  });

  // Require one of targetFinalLitres or targetLitresArriving to be defined at minimum
  const toVesselVolumeIsRequired = computed(() => {
    return !vesselMovement.value.toVesselDetails?.targetFinalLitres &&
      !vesselMovement.value.toVesselDetails?.targetLitresArriving
      ? [ruleSet.required()]
      : [];
  });

  // If we are in double
  const vesselMovementSectionColumns = computed(() => {
    switch (vesselMovementType.value) {
      case EnumVesselMovementType.vesselToVessel:
      case EnumVesselMovementType.vesselToExistingBatch:
      case EnumVesselMovementType.vesselToExistingPackaging:
      case EnumVesselMovementType.vesselToNewBatch:
      case EnumVesselMovementType.vesselToNewPackaging:
        return 6;

      default:
        return 12;
    }
  });

  const lockMovementToMeasuredVolumeOnly = computed(() =>
    vesselMovementType.value.isUpdateVolume()
  );

  const vesselMovementIsToExternalAssembly = computed(() => {
    switch (vesselMovementType.value) {
      case EnumVesselMovementType.vesselToExistingBatch:
      case EnumVesselMovementType.vesselToExistingPackaging:
      case EnumVesselMovementType.vesselToNewBatch:
      case EnumVesselMovementType.vesselToNewPackaging:
        return true;

      default:
        return false;
    }
  });

  const vesselMovementIsOptional = computed(() => {
    switch (vesselMovementType.value) {
      case EnumVesselMovementType.vesselToExistingBatch:
      case EnumVesselMovementType.vesselToExistingPackaging:
      case EnumVesselMovementType.vesselToNewBatch:
      case EnumVesselMovementType.vesselToNewPackaging:
        return true;

      default:
        return false;
    }
  });

  watch(includeVesselMovementWithOutput, () => {
    if (includeVesselMovementWithOutput.value) {
      vesselMovement.value.toVesselDetails = new ToVesselDetails();
      vesselMovement.value.targetType = EnumVesselMovementTargetType.vessel;
      vesselMovement.value.toVesselDetails.targetLitresArriving =
        vesselMovement.value.fromVesselDetails?.sourceLitresLeaving ?? 0;
    } else {
      vesselMovement.value.toVesselDetails = null;
      vesselMovement.value.targetType = EnumVesselMovementTargetType.output;
    }
  });

  return {
    // Boolean state vars
    movementRequiresFromVessel,
    movementRequiresToVessel,
    lockMovementToMeasuredVolumeOnly,
    vesselMovementIsToExternalAssembly,
    vesselMovementIsOptional,
    includeVesselMovementWithOutput,

    // Rulesets for controls
    fromVesselVolumeIsRequired,
    toVesselVolumeIsRequired,

    // Columns
    vesselMovementSectionColumns,
  };
}

export class EnumVesselMovementType implements BaseEnumType {
  private static allValues: EnumVesselMovementType[] = [];

  public static vesselToVessel = new EnumVesselMovementType(
    0,
    "Vessel To Vessel",
    "vessel_logs_add_vessel_movement",
    EnumVesselMovementSourceType.vessel,
    EnumVesselMovementTargetType.vessel,
    true,
    false,
    true
  );
  public static updateVolume = new EnumVesselMovementType(
    1,
    "Update Volume",
    "vessel_logs_update_volume",
    EnumVesselMovementSourceType.measurement,
    EnumVesselMovementTargetType.vessel,
    false,
    false,
    true
  );
  public static addIngredientsToVessel = new EnumVesselMovementType(
    2,
    "Ingredients to Vessel",
    "vessel_logs_add_ingredients_to_vessel",
    EnumVesselMovementSourceType.ingredients,
    EnumVesselMovementTargetType.vessel,
    false,
    false,
    true
  );
  public static addOutput = new EnumVesselMovementType(
    3,
    "Vessel Output",
    "vessel_logs_output",
    EnumVesselMovementSourceType.vessel,
    EnumVesselMovementTargetType.output,
    false,
    true,
    false
  );
  public static vesselToExistingBatch = new EnumVesselMovementType(
    4,
    "Vessel To Existing Batch",
    "vessel_logs_add_vessel_movement",
    EnumVesselMovementSourceType.vessel,
    EnumVesselMovementTargetType.output,
    true,
    true,
    true
  );
  public static vesselToExistingPackaging = new EnumVesselMovementType(
    5,
    "Vessel To Existing Packaging",
    "vessel_logs_add_vessel_movement",
    EnumVesselMovementSourceType.vessel,
    EnumVesselMovementTargetType.output,
    true,
    true,
    false
  );
  public static vesselToNewBatch = new EnumVesselMovementType(
    6,
    "Vessel To New Batch",
    "vessel_logs_add_vessel_movement",
    EnumVesselMovementSourceType.vessel,
    EnumVesselMovementTargetType.output,
    true,
    true,
    true
  );
  public static vesselToNewPackaging = new EnumVesselMovementType(
    7,
    "Vessel To New Packaging",
    "vessel_logs_add_vessel_movement",
    EnumVesselMovementSourceType.vessel,
    EnumVesselMovementTargetType.output,
    true,
    true,
    false
  );

  public key: number;
  public value: string;
  public translationKey: string;
  public sourceType: EnumVesselMovementSourceType;
  public targetType: EnumVesselMovementTargetType;
  public hasInterVesselMovement: boolean;
  public isOutputtingMovement: boolean;
  public prefersToTargetVessel: boolean;

  private constructor(
    key: number,
    value: string,
    translationKey: string,
    sourceType: EnumVesselMovementSourceType,
    targetType: EnumVesselMovementTargetType,
    hasInterVesselMovement: boolean,
    isOutputtingMovement: boolean,
    prefersToTargetVessel: boolean
  ) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    this.sourceType = sourceType;
    this.targetType = targetType;
    this.hasInterVesselMovement = hasInterVesselMovement;
    this.isOutputtingMovement = isOutputtingMovement;
    this.prefersToTargetVessel = prefersToTargetVessel;
    EnumVesselMovementType.allValues.push(this);
  }

  public get translation() {
    return translate(Key.fromString(this.translationKey));
  }

  public equals(other: Maybe<EnumVesselMovementType>): boolean {
    return this.key === other?.key;
  }

  public isVesselToVessel() {
    return this.equals(EnumVesselMovementType.vesselToVessel);
  }
  public isUpdateVolume() {
    return this.equals(EnumVesselMovementType.updateVolume);
  }
  public isAddIngredientsToVessel() {
    return this.equals(EnumVesselMovementType.addIngredientsToVessel);
  }
  public isAddOutput() {
    return this.equals(EnumVesselMovementType.addOutput);
  }
  public isVesselToExistingBatch() {
    return this.equals(EnumVesselMovementType.vesselToExistingBatch);
  }
  public isVesselToExistingPackaging() {
    return this.equals(EnumVesselMovementType.vesselToExistingPackaging);
  }
  public isVesselToNewBatch() {
    return this.equals(EnumVesselMovementType.vesselToNewBatch);
  }
  public isVesselToNewPackaging() {
    return this.equals(EnumVesselMovementType.vesselToNewPackaging);
  }

  public static getValues(): EnumVesselMovementType[] {
    return EnumVesselMovementType.allValues;
  }

  public static getByKey(key: number): EnumVesselMovementType {
    const found = EnumVesselMovementType.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumVesselMovementType - Key not found: ${key}`);
    }

    return found;
  }
}
